import { useState, useEffect } from "react";
import { API , authFetch, useEditMode, useAuth } from "@/App";
import { toast } from "sonner";
import { mutate, confirmDelete } from "@/lib/apiMutate";
import { SyncNowButton } from "@/components/SyncNowButton";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";
import { Gift, Plus, Search, MoreHorizontal, Edit, Trash2, Sparkles, Loader2, Users, Zap, CheckCircle2, Lightbulb } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const SchemesPage = () => {
  const canEdit = useEditMode();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  // AI Generator state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  // Scheme enhancements
  const [trackingScheme, setTrackingScheme] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [boostScheme, setBoostScheme] = useState(null);
  const [boostData, setBoostData] = useState(null);
  const [boostLoading, setBoostLoading] = useState(false);
  const [prefix, setPrefix] = useState("");
  const [prefixDialogOpen, setPrefixDialogOpen] = useState(false);
  const [closingScheme, setClosingScheme] = useState(null);
  const [closeResult, setCloseResult] = useState(null);
  const [formData, setFormData] = useState({
    scheme_name: "",
    scheme_type: "ORDER_VALUE_DISCOUNT",
    valid_from: "",
    valid_to: "",
    discount_value: 0,
    min_quantity: 0,
    terms_conditions: "",
    image_url: "",
    terms_doc_url: "",
    status: "DRAFT",
  });

  useEffect(() => {
    fetchSchemes();
    // Fetch prefix config
    authFetch(`${API}/schemes/config/prefix`)
      .then(r => r.json())
      .then(d => { if (d.success) setPrefix(d.data?.prefix || ""); })
      .catch(() => {});
  }, [statusFilter]);

  // Scheme enhancement handlers
  const openTracking = async (scheme) => {
    setTrackingScheme(scheme);
    setTrackingLoading(true);
    try {
      const res = await authFetch(`${API}/schemes/${scheme.scheme_id}/dealer-tracking`);
      const d = await res.json();
      if (d.success) setTrackingData(d.data);
    } catch { toast.error("Failed to load tracking"); }
    finally { setTrackingLoading(false); }
  };

  const runAiBoost = async (scheme) => {
    setBoostScheme(scheme);
    setBoostData(null);
    setBoostLoading(true);
    try {
      const res = await authFetch(`${API}/schemes/${scheme.scheme_id}/ai-boost`, { method: "POST" });
      const d = await res.json();
      if (d.success) setBoostData(d.data);
      else toast.error(d.detail || "AI boost failed");
    } catch { toast.error("Network error"); }
    finally { setBoostLoading(false); }
  };

  const savePrefix = async () => {
    try {
      const res = await authFetch(`${API}/schemes/config/prefix`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prefix }),
      });
      const d = await res.json();
      if (d.success) {
        toast.success(`Prefix set to "${d.data.prefix || 'none'}"`);
        setPrefixDialogOpen(false);
      }
    } catch { toast.error("Failed to save prefix"); }
  };

  const autoCloseScheme = async (scheme) => {
    if (!window.confirm(`Close "${scheme.scheme_name}" and auto-tally all dealer claims? This is irreversible.`)) return;
    setClosingScheme(scheme);
    try {
      const res = await authFetch(`${API}/schemes/${scheme.scheme_id}/auto-tally-close`, { method: "POST" });
      const d = await res.json();
      if (d.success) {
        setCloseResult(d.data);
        toast.success(`${d.data.claims_created} claims created`);
        fetchSchemes();
      } else toast.error(d.detail || "Failed to close");
    } catch { toast.error("Network error"); }
    finally { setClosingScheme(null); }
  };

  const fetchSchemes = async () => {
    try {
      let url = `${API}/schemes?`;
      if (statusFilter && statusFilter !== "all") url += `status=${statusFilter}&`;

      const response = await authFetch(url, {  });
      const data = await response.json();
      if (data.success) {
        setSchemes(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch schemes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingScheme ? `${API}/schemes/${editingScheme.scheme_id}` : `${API}/schemes`;
    const r = await mutate(
      authFetch,
      url,
      {
        method: editingScheme ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      },
      {
        successMessage: editingScheme ? "Scheme updated" : "Scheme created",
        failureMessage: "Failed to save scheme",
      },
    );
    if (!r.ok) return;
    setIsDialogOpen(false);
    setEditingScheme(null);
    resetForm();
    fetchSchemes();
  };

  const openEditDialog = (scheme) => {
    setEditingScheme(scheme);
    setFormData({
      scheme_name: scheme.scheme_name,
      scheme_type: scheme.scheme_type,
      valid_from: scheme.valid_from,
      valid_to: scheme.valid_to,
      discount_value: scheme.discount_value || 0,
      min_quantity: scheme.min_quantity || 0,
      terms_conditions: scheme.terms_conditions || "",
      image_url: scheme.image_url || "",
      terms_doc_url: scheme.terms_doc_url || "",
      status: scheme.status,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      scheme_name: "",
      scheme_type: "ORDER_VALUE_DISCOUNT",
      valid_from: "",
      valid_to: "",
      discount_value: 0,
      min_quantity: 0,
      terms_conditions: "",
      image_url: "",
      terms_doc_url: "",
      status: "DRAFT",
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: "badge-default",
      ACTIVE: "badge-success",
      PAUSED: "bg-amber-100 text-amber-700",
      EXPIRED: "badge-danger",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || "badge-default"}`}>
        {status}
      </span>
    );
  };

  const handlePause = async (schemeId) => {
    const r = await mutate(authFetch, `${API}/schemes/${schemeId}/pause`, { method: "POST" },
      { successMessage: "Scheme paused", failureMessage: "Failed to pause" });
    if (r.ok) fetchSchemes();
  };

  const handleResume = async (schemeId) => {
    const r = await mutate(authFetch, `${API}/schemes/${schemeId}/resume`, { method: "POST" },
      { successMessage: "Scheme resumed", failureMessage: "Failed to resume" });
    if (r.ok) fetchSchemes();
  };

  const handleReset = async (schemeId) => {
    if (!window.confirm("Reset all redemptions for this scheme?")) return;
    const r = await mutate(authFetch, `${API}/schemes/${schemeId}/reset`, { method: "POST" },
      { successMessage: "Scheme reset", failureMessage: "Failed to reset" });
    if (r.ok) fetchSchemes();
  };

  const handleDelete = async (schemeId) => {
    const r = await confirmDelete(authFetch, `${API}/schemes/${schemeId}`, {
      promptMessage: "Permanently delete this scheme?",
      successMessage: "Scheme deleted",
      failureMessage: "Failed to delete",
    });
    if (r.ok) fetchSchemes();
  };

  const getTypeBadge = (type) => {
    const labels = {
      BUY_X_GET_Y: "Buy X Get Y",
      BUY_X_GET_DISCOUNT: "Buy X Get Discount",
      ORDER_VALUE_DISCOUNT: "Order Value Discount",
      BUNDLE_OFFER: "Bundle Offer",
      QUANTITY_BONUS: "Quantity Bonus",
      LOYALTY_REWARD: "Loyalty Reward",
      SEASONAL: "Seasonal",
    };
    return (
      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
        {labels[type] || type}
      </Badge>
    );
  };

  const generateAIScheme = async () => {
    if (!aiPrompt.trim()) { toast.error("Please describe the scheme you want"); return; }
    setAiLoading(true);
    setAiResult(null);
    try {
      const res = await authFetch(`${API}/ai/generate-scheme`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: aiPrompt }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        setAiResult(data.data);
      } else {
        toast.error("AI generation failed");
      }
    } catch { toast.error("Failed to generate scheme"); }
    finally { setAiLoading(false); }
  };

  const applyAIScheme = () => {
    if (!aiResult) return;
    setFormData({
      scheme_name: aiResult.scheme_name || "",
      scheme_type: aiResult.scheme_type || "ORDER_VALUE_DISCOUNT",
      valid_from: aiResult.valid_from || "",
      valid_to: aiResult.valid_to || "",
      discount_value: aiResult.discount_value || 0,
      min_quantity: aiResult.min_quantity || 0,
      terms_conditions: aiResult.terms_conditions || "",
      status: "DRAFT",
    });
    setAiOpen(false);
    setIsDialogOpen(true);
    toast.success("AI scheme applied! Review and save.");
  };

  return (
    <div className="space-y-6" data-testid="schemes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Schemes</h1>
          <p className="text-slate-500 text-sm mt-1">Manage promotional offers</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="schemes-region-switcher"
          />
          <SyncNowButton entity="schemes" />
          {canEdit && (
            <>
            <Button
              variant="outline"
              onClick={() => setPrefixDialogOpen(true)}
              className="border-slate-300"
              data-testid="prefix-config-btn"
              title="Scheme code prefix"
            >
              <Edit className="w-4 h-4 mr-1.5" /> Prefix {prefix && <Badge variant="outline" className="ml-1 text-[10px]">{prefix}</Badge>}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setAiPrompt(""); setAiResult(null); setAiOpen(true); }}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="ai-scheme-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" /> AI Generator
            </Button>
            <Button
              onClick={() => {
                setEditingScheme(null);
                resetForm();
                setIsDialogOpen(true);
              }}
              className="btn-primary"
              data-testid="add-scheme-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Scheme
            </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="DRAFT">Draft</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="PAUSED">Paused</SelectItem>
              <SelectItem value="EXPIRED">Expired</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="ORDER_VALUE_DISCOUNT">Order Value Discount</SelectItem>
              <SelectItem value="BUY_X_GET_Y">Buy X Get Y</SelectItem>
              <SelectItem value="QUANTITY_BONUS">Quantity Bonus</SelectItem>
              <SelectItem value="LOYALTY_REWARD">Loyalty Reward</SelectItem>
              <SelectItem value="SEASONAL">Seasonal</SelectItem>
              <SelectItem value="BUNDLE_OFFER">Bundle Offer</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Schemes Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 spinner"></div>
        </div>
      ) : schemes.length === 0 ? (
        <Card className="p-8">
          <div className="empty-state">
            <Gift className="empty-state-icon" />
            <p className="empty-state-title">No schemes found</p>
            <p className="empty-state-description">
              Create promotional schemes to boost sales.
            </p>
            <Button onClick={() => setIsDialogOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Scheme
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filterByRegion(schemes, (s) => s.region_id).map((scheme) => (
            <Card
              key={scheme.scheme_id}
              className="p-6 hover:shadow-md transition-shadow"
              data-testid={`scheme-card-${scheme.scheme_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Gift className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(scheme.status)}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(scheme)}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openTracking(scheme)} data-testid={`tracking-${scheme.scheme_id}`}>
                        <Users className="w-4 h-4 mr-2" /> Dealer Tracking
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => runAiBoost(scheme)} data-testid={`ai-boost-${scheme.scheme_id}`}>
                        <Zap className="w-4 h-4 mr-2" /> AI Boost Ideas
                      </DropdownMenuItem>
                      {scheme.status === "ACTIVE" && (
                        <DropdownMenuItem onClick={() => handlePause(scheme.scheme_id)}>
                          Pause Scheme
                        </DropdownMenuItem>
                      )}
                      {scheme.status === "PAUSED" && (
                        <DropdownMenuItem onClick={() => handleResume(scheme.scheme_id)}>
                          Resume Scheme
                        </DropdownMenuItem>
                      )}
                      {scheme.status !== "CLOSED" && (
                        <DropdownMenuItem onClick={() => autoCloseScheme(scheme)} className="text-amber-700" data-testid={`close-${scheme.scheme_id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-2" /> Close & Auto-Tally
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleReset(scheme.scheme_id)}>
                        Reset Redemptions
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(scheme.scheme_id)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <h3 className="font-semibold text-slate-900 mb-2">{scheme.scheme_name}</h3>
              <div className="mb-3">{getTypeBadge(scheme.scheme_type)}</div>

              <div className="space-y-2 text-sm">
                {scheme.discount_value && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Discount</span>
                    <span className="font-medium text-[#8B1A1A]">{scheme.discount_value}%</span>
                  </div>
                )}
                {scheme.min_quantity && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Min. Quantity</span>
                    <span className="font-medium">{scheme.min_quantity} bags</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Valid</span>
                  <span className="font-medium">{scheme.valid_from} - {scheme.valid_to}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Redemptions</span>
                  <span className="font-medium font-mono">{scheme.current_redemptions || 0}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="scheme-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingScheme ? "Edit Scheme" : "Create Scheme"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Scheme Name</label>
              <Input
                value={formData.scheme_name}
                onChange={(e) => setFormData({ ...formData, scheme_name: e.target.value })}
                placeholder="Monsoon Bonanza 2025"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Type</label>
                <Select
                  value={formData.scheme_type}
                  onValueChange={(value) => setFormData({ ...formData, scheme_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ORDER_VALUE_DISCOUNT">Order Value Discount</SelectItem>
                    <SelectItem value="BUY_X_GET_Y">Buy X Get Y</SelectItem>
                    <SelectItem value="BUY_X_GET_DISCOUNT">Buy X Get Discount</SelectItem>
                    <SelectItem value="BUNDLE_OFFER">Bundle Offer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRAFT">Draft</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Valid From</label>
                <Input
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Valid To</label>
                <Input
                  type="date"
                  value={formData.valid_to}
                  onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Discount (%)</label>
                <Input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                  placeholder="5"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Min. Quantity</label>
                <Input
                  type="number"
                  value={formData.min_quantity}
                  onChange={(e) => setFormData({ ...formData, min_quantity: parseInt(e.target.value) })}
                  placeholder="100"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Terms & Conditions</label>
              <Textarea
                value={formData.terms_conditions}
                onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                placeholder="Scheme terms..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Scheme image / poster URL</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://… (jpg/png)"
                  data-testid="scheme-image-url-input"
                />
                {formData.image_url && (
                  <img src={formData.image_url} alt="Scheme preview" className="mt-2 max-h-28 rounded border" />
                )}
                <p className="text-[10px] text-slate-500 mt-1">Used as the share asset when salesmen WhatsApp the scheme to dealers.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">T&C document URL (PDF / DOC)</label>
                <Input
                  value={formData.terms_doc_url}
                  onChange={(e) => setFormData({ ...formData, terms_doc_url: e.target.value })}
                  placeholder="https://… (downloadable)"
                  data-testid="scheme-terms-doc-input"
                />
                {formData.terms_doc_url && (
                  <a href={formData.terms_doc_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs text-[#8B1A1A] underline" data-testid="scheme-terms-doc-link">
                    Preview document ↗
                  </a>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                {editingScheme ? "Update" : "Create"} Scheme
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Scheme Generator Dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg" data-testid="ai-scheme-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" /> AI Scheme Generator</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Describe the scheme you want</label>
              <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g. Create a monsoon scheme for Gujarat dealers with 5% discount on OPC 53 Grade for orders above 100 bags, valid June to August"
                className="min-h-[80px]" data-testid="ai-prompt-input" />
            </div>
            <Button onClick={generateAIScheme} disabled={aiLoading || !aiPrompt.trim()}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="generate-scheme-btn">
              {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Scheme</>}
            </Button>
            {aiResult && !aiResult.error && (
              <div className="bg-purple-50 rounded-lg p-4 space-y-3 border border-purple-100" data-testid="ai-result">
                <h4 className="font-semibold text-purple-900">{aiResult.scheme_name}</h4>
                <p className="text-sm text-purple-700">{aiResult.description}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Type:</span> <span className="font-medium">{aiResult.scheme_type}</span></div>
                  <div><span className="text-slate-500">Discount:</span> <span className="font-medium">{aiResult.discount_value}%</span></div>
                  <div><span className="text-slate-500">Min Qty:</span> <span className="font-medium">{aiResult.min_quantity} bags</span></div>
                  <div><span className="text-slate-500">Region:</span> <span className="font-medium">{aiResult.target_region}</span></div>
                  <div><span className="text-slate-500">From:</span> <span className="font-medium">{aiResult.valid_from}</span></div>
                  <div><span className="text-slate-500">To:</span> <span className="font-medium">{aiResult.valid_to}</span></div>
                </div>
                {aiResult.terms_conditions && (
                  <div className="text-xs text-purple-600 whitespace-pre-line">{typeof aiResult.terms_conditions === "string" ? aiResult.terms_conditions : JSON.stringify(aiResult.terms_conditions)}</div>
                )}
                <Button onClick={applyAIScheme} className="w-full btn-primary" data-testid="apply-ai-scheme">
                  Use This Scheme
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Scheme Prefix Config Dialog */}
      <Dialog open={prefixDialogOpen} onOpenChange={setPrefixDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="prefix-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="w-4 h-4" /> Scheme Code Prefix</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500">Set a prefix used for newly-created scheme IDs (e.g. <code className="bg-slate-100 px-1">DIWALI</code> → IDs like <code className="bg-slate-100 px-1">SCH-DIWALI-AB12CD34</code>). Leave empty to disable.</p>
            <Input value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} placeholder="e.g. DIWALI, Q1FY26, ONAM" maxLength={20} data-testid="prefix-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setPrefixDialogOpen(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={savePrefix} data-testid="save-prefix-btn">Save Prefix</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dealer Tracking Dialog */}
      <Dialog open={!!trackingScheme} onOpenChange={(o) => { if (!o) { setTrackingScheme(null); setTrackingData(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tracking-dialog">
          {trackingScheme && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#8B1A1A]" /> Dealer Tracking — {trackingScheme.scheme_name}
                </DialogTitle>
              </DialogHeader>
              {trackingLoading ? (
                <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
              ) : !trackingData ? (
                <p className="text-sm text-slate-400 text-center py-8">No data</p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <Card className="p-3 text-center"><p className="text-xs text-slate-500">Total Orders</p><p className="text-xl font-mono font-bold">{trackingData.total_orders}</p></Card>
                    <Card className="p-3 text-center"><p className="text-xs text-slate-500">Total Amount</p><p className="text-xl font-mono font-bold text-emerald-600">₹{((trackingData.total_amount || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</p></Card>
                    <Card className="p-3 text-center"><p className="text-xs text-slate-500">Dealers</p><p className="text-xl font-mono font-bold text-blue-600">{trackingData.dealers?.length || 0}</p></Card>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="data-table text-sm">
                      <thead><tr><th>Dealer</th><th>Category</th><th className="text-right">Orders</th><th className="text-right">Bags</th><th className="text-right">Amount</th></tr></thead>
                      <tbody>
                        {(trackingData.dealers || []).map((d, i) => (
                          <tr key={i}>
                            <td className="font-medium">{d.firm_name}</td>
                            <td><Badge variant="outline" className="text-[10px]">{d.category || "-"}</Badge></td>
                            <td className="text-right font-mono">{d.orders}</td>
                            <td className="text-right font-mono">{d.bags}</td>
                            <td className="text-right font-mono font-bold text-emerald-600">₹{((d.amount || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Boost Dialog */}
      <Dialog open={!!boostScheme} onOpenChange={(o) => { if (!o) { setBoostScheme(null); setBoostData(null); } }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="boost-dialog">
          {boostScheme && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" /> AI Boost Ideas — {boostScheme.scheme_name}
                </DialogTitle>
              </DialogHeader>
              {boostLoading ? (
                <div className="py-12 text-center space-y-3">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" />
                  <p className="text-sm text-slate-500">Analysing scheme performance with Claude...</p>
                </div>
              ) : boostData ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-2">
                    <Card className="p-2 text-center"><p className="text-[10px] text-slate-500 uppercase">Achieved</p><p className="text-sm font-bold font-mono">{boostData.performance.achievement_pct}%</p></Card>
                    <Card className="p-2 text-center"><p className="text-[10px] text-slate-500 uppercase">Amount</p><p className="text-sm font-bold font-mono">₹{(boostData.performance.total_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</p></Card>
                    <Card className="p-2 text-center"><p className="text-[10px] text-slate-500 uppercase">Bags</p><p className="text-sm font-bold font-mono">{boostData.performance.total_bags}</p></Card>
                    <Card className="p-2 text-center"><p className="text-[10px] text-slate-500 uppercase">Dealers</p><p className="text-sm font-bold font-mono">{boostData.performance.participating_dealers}</p></Card>
                  </div>
                  {boostData.ai?.summary && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-semibold text-amber-800 mb-1">AI Summary</p>
                      <p className="text-sm text-amber-900">{boostData.ai.summary}</p>
                    </div>
                  )}
                  {(boostData.ai?.suggestions || []).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Suggested Actions</p>
                      {boostData.ai.suggestions.map((s, i) => (
                        <Card key={i} className="p-3" data-testid={`boost-suggestion-${i}`}>
                          <div className="flex items-start gap-2">
                            <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-sm text-slate-800">{s.title}</p>
                                {s.impact && <Badge className={`text-[9px] ${s.impact === "High" ? "bg-red-100 text-red-700" : s.impact === "Medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{s.impact} impact</Badge>}
                              </div>
                              <p className="text-xs text-slate-600 mt-1">{s.action}</p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Auto-Close Result Dialog */}
      <Dialog open={!!closeResult} onOpenChange={(o) => { if (!o) setCloseResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="close-result-dialog">
          {closeResult && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Scheme Closed &amp; Tallied
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                  <p className="text-3xl font-bold text-emerald-700 font-mono">{closeResult.claims_created}</p>
                  <p className="text-sm text-emerald-600">dealer claims auto-created</p>
                  <p className="text-sm text-emerald-800 mt-2">Total claim value: <strong>₹{closeResult.total_claim_value_rs.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</strong></p>
                </div>
                <p className="text-xs text-slate-500 text-center">Claims are in PENDING_APPROVAL state and can be reviewed / approved by admins.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchemesPage;
